import { CSSProperties } from "react";
import { SxProps, Theme } from "@mui/material";

/** Column header top/bottom keyline — Chat Dez. */
export const DATA_GRID_HEADER_KEYLINE = "1px solid #00000063";

/**
 * Prefer this over fontWeight bold/700 — use the Roboto Bold face itself.
 * Matches DataGrid column headers and Header.tsx entity titles.
 */
export const ROBOTO_BOLD_FONT_FAMILY = '"Roboto-Bold", "Roboto", sans-serif';

/** Bold text via Roboto-Bold (fontWeight stays normal so the face is not double-bolded). */
export const robotoBoldSx = {
  fontFamily: ROBOTO_BOLD_FONT_FAMILY,
  fontWeight: 400,
} as const;

/** Divider under each detail-tab totals row (Parts / Misc / Freight, Quote, PO). */
export const DETAIL_TOTALS_SUMMARY_ROW_BORDER = "1px solid rgba(224, 224, 224, 1)";

/**
 * Compact right-aligned totals row (Sales Order Parts / Misc / Freight style).
 * Spread onto each label|value row; use `borderBottom: "none"` on the last row.
 */
export const detailTotalsSummaryRowSx = {
  display: "grid",
  gridTemplateColumns: "auto minmax(100px, max-content)",
  columnGap: 2,
  justifyContent: "end",
  alignItems: "center",
  width: "100%",
  py: 1.5,
  px: 1,
  borderBottom: DETAIL_TOTALS_SUMMARY_ROW_BORDER,
  color: "#616161",
  fontSize: "14px",
  ...robotoBoldSx,
} as const;

export const detailTotalsSummaryCellSx = {
  textAlign: "right" as const,
  whiteSpace: "nowrap" as const,
};

/** List/DataGrid column cell links (numeric IDs, stock/demand counts, etc.). */
export const LIST_CELL_LINK_COLOR = "#4282b8";
export const LIST_CELL_LINK_HOVER_COLOR = "#336a96";

export const listCellLinkStyle: CSSProperties = {
  color: LIST_CELL_LINK_COLOR,
  fontWeight: "bold",
  textDecoration: "none",
};

/**
 * A cell holding only its column's placeholder ("-": no due date on an hours item, no
 * job yet). It reads quieter than a value so the values stand out down a column; the
 * text itself is unchanged, so sorting, filtering and export see the same "-". A column
 * tags it with `cellClassName`; the grid spreads `emptyCellCssSx` into its `sx`.
 */
export const EMPTY_CELL_CLASS = "an-cell--empty";

export const emptyCellCssSx = {
  [`& .MuiDataGrid-cell.${EMPTY_CELL_CLASS}`]: { color: "var(--an-muted-2)" },
};

/** CSS class applied by useRenderLink / useAdvanceSearchLink for hover. */
export const LIST_CELL_LINK_CLASS = "list-cell-link";

/** Spread into DataGrid `sx` so list cell link hover works (incl. AeronetDataGrid). */
export const listCellLinkCssSx = {
  [`& .MuiDataGrid-cell a.${LIST_CELL_LINK_CLASS}`]: {
    color: `${LIST_CELL_LINK_COLOR} !important`,
    fontWeight: "bold",
    textDecoration: "none",
    "&:hover": {
      color: `${LIST_CELL_LINK_HOVER_COLOR} !important`,
    },
  },
};

const dataGridHeaderKeylineSx = {
  "& .MuiDataGrid-columnHeaders": {
    borderTop: `${DATA_GRID_HEADER_KEYLINE} !important`,
    borderBottom: `${DATA_GRID_HEADER_KEYLINE} !important`,
    borderRadius: "0px !important",
  },
  // Premium/Pro draw the header bottom keyline via ::after using --DataGrid-rowBorderColor
  // (lighter grey). Force it to the same Chat Dez keyline as the top border.
  "& .MuiDataGrid-container--top::after": {
    backgroundColor: "#00000063 !important",
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    paddingBottom: 0,
  },
};

/**
 * The listing GUTTER — the single horizontal inset every listing surface sits on.
 *
 * `AeronetDataGrid` is self-insetting: DataGrid.tsx puts `margin: 0px 50px` on
 * `.MuiDataGrid-main` and `.MuiDataGrid-footerContainer`, and the toolbar below
 * carries the matching `pl`. The app shell (components/AppBackground.tsx) supplies
 * NO horizontal padding at all, so this 50px IS the left edge of the content box
 * on every grid module — which is why a module that renders the grid straight
 * under its header is already aligned, and why an extra padded wrapper around the
 * grid stacks ON TOP of it and pushes that one module in.
 *
 * Exported so a surface that is NOT a DataGrid (a hand-rolled board, a calendar)
 * can sit on the same edge instead of guessing a number. Read the sweep note in
 * blank/PATTERNS.md §"Aligning a listing surface" before changing the value: it
 * moves every module at once.
 */
export const LISTING_GUTTER = "50px";

/** List DataGrid toolbar — more space above + / ACTIONS / Find, tight to the keyline. */
export const listToolbarContainerSx = {
  alignItems: "center" as const,
  pt: { xs: 2, md: 5 },
  pr: { xs: 1, md: 6 },
  pb: { xs: 2.5, md: 2.5 },
  pl: { xs: 1, md: "50px" },
};

/**
 * A LIST screen on `AeronetDataGridPremium` (`layout="list"`): the Pro list's own
 * edge in place of the detail-tab spacing. The toolbar sits on
 * `listToolbarContainerSx`, and the body and the footer are inset by LISTING_GUTTER,
 * as DataGrid.tsx does for every Pro list. Without it a Premium list ran edge to edge
 * and its (+) sat half off the page (the Onboarding Checklist, Aaron 2026-10-20: "why
 * is it not following patterns?").
 */
export const listingPremiumGridSx = {
  marginTop: 0,
  "& .MuiDataGrid-toolbarContainer": listToolbarContainerSx,
  "& .MuiDataGrid-main": {
    margin: `0px ${LISTING_GUTTER} !important`,
    borderTop: `${DATA_GRID_HEADER_KEYLINE} !important`,
  },
  "& .MuiDataGrid-footerContainer": { margin: `0px ${LISTING_GUTTER} !important` },
};

/**
 * BP-38, an EMPHASIS row: a row a server flag marks for attention (the Onboarding
 * Checklist's decision gates, `is_gate`). The row is tinted and its key cell's text,
 * wrapped in EMPHASIS_TEXT_CLASS, is red and bold, all from the shared tokens. Spread
 * `emphasisRowSx` into the grid's `sx`; give the row EMPHASIS_ROW_CLASS through
 * `getRowClassName`. The module supplies the flag, never a colour.
 */
export const EMPHASIS_ROW_CLASS = "an-emphasis-row";
export const EMPHASIS_TEXT_CLASS = "an-emphasis-text";
export const emphasisRowSx = {
  [`& .${EMPHASIS_ROW_CLASS}, & .${EMPHASIS_ROW_CLASS}:hover`]: { bgcolor: "var(--an-red-tint)" },
  [`& .${EMPHASIS_ROW_CLASS} .${EMPHASIS_TEXT_CLASS}`]: { color: "var(--an-red)", fontWeight: 700 },
};

/**
 * No focus ring on grid cells or on the checkbox column header (H95).
 *
 * MUI X draws `:focus` AND `:focus-within` outlines on `.MuiDataGrid-cell` and
 * `.MuiDataGrid-columnHeader` (Pro 6.9 and Premium 7.1 alike). Ticking a row
 * focuses the <input> INSIDE the checkbox cell, so `:focus-within` boxed the
 * cell — Aaron 2026-09-28: "there is a boarder around action check boxes".
 * The Pro wrapper only ever cleared `cell:focus`; Premium cleared nothing.
 *
 * Applied ONCE, as the theme's `MuiDataGrid` root override (theme.ts), so it
 * reaches every grid — both wrappers and the module-local ones. Do not re-add a
 * cell outline per module.
 *
 * Editable cells are left alone on purpose: before editing, their focus ring is
 * the signal that the cell takes input (RFQRootDataGrid keeps it), and the
 * editing cell's own `.MuiDataGrid-cell--editing:focus-within` ring is more
 * specific than these rules anyway. Keyboard users therefore lose the ring on
 * read-only cells, exactly as the Pro wrapper's `cell:focus` rule already did.
 */
export const dataGridNoFocusOutlineSx = {
  "& .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable):focus, & .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable):focus-within":
    {
      outline: "none",
    },
  "& .MuiDataGrid-columnHeaderCheckbox:focus, & .MuiDataGrid-columnHeaderCheckbox:focus-within":
    {
      outline: "none",
    },
} as const;

/**
 * A grid cell's own side padding (MUI X 7 `.MuiDataGrid-cell`: `0 10px`). A cell that is not
 * a DataGrid cell but sits among grid text (a plain board's label column) takes this, so its
 * text lines up with the grid's.
 */
export const GRID_CELL_PADDING_X = "10px";

/** A tick in a Premium grid's FIRST column, cell and header (commonDataGridPremiumSx insets it). */
export const FIRST_COLUMN_TICK_SELECTOR =
  "& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox[data-colindex='0'], & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox[aria-colindex='1']";

export const commonDataGridSx: SxProps<Theme> = {
  borderLeft: "none",
  borderRight: "none",
  borderTop: "none !important",
  ...dataGridHeaderKeylineSx,

  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: "bolder",
  },

  "& .MuiDataGrid-main": {
    margin: "0px 0px !important",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
  },

  "& .MuiDataGrid-footerContainer": {
    margin: "0px 0px !important",
    borderBottom: `${DATA_GRID_HEADER_KEYLINE} !important`,
    marginBottom: "10px",
  },

  "& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox": {
    padding: "0 !important",
    justifyContent: "flex-start",
  },
  "& .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root, & .MuiDataGrid-checkboxInput": {
    padding: "0 !important",
    margin: 0,
  },
};


/**
 * A master/detail row taller than one line (getRowHeight "auto") keeps its tick
 * and its reveal chevron together on the FIRST line, as on a single-line row
 * (Aaron 2026-10-06: "align the action tick box with the reveal"). Left alone,
 * MUI centres the tick (a flex cell) over the whole row while the chevron (a
 * block cell) sits at the top. Each control gets at least the density's base row
 * height and centres in it, so both line up with the first line of the row's text
 * (a stacked row's first line is the row's own content box, so it centres there too:
 * STACK_ROW_PX / STACK_LINE_PX in components/datagrid/StackedLines.tsx).
 * Only rows that have a reveal: auto-height lists without one keep their own
 * alignment (BP grouped lists centre every cell on purpose).
 */
/** A grid row's height per density (MUI X 7: 52px × the density factor, 0.7 / 1 / 1.3, floored). */
export const DENSITY_ROW_HEIGHT = { Compact: 36, Standard: 52, Comfortable: 67 } as const;
const detailRowControlsOnFirstLineSx = Object.fromEntries(
  (Object.keys(DENSITY_ROW_HEIGHT) as (keyof typeof DENSITY_ROW_HEIGHT)[]).map((density) => [
    `&.MuiDataGrid-root--density${density} .MuiDataGrid-row--dynamicHeight:has(> [data-field="__detail_panel_toggle__"]) > .MuiDataGrid-cell:is([data-field="__check__"], [data-field="__detail_panel_toggle__"])`,
    {
      display: "flex",
      alignItems: "flex-start",
      // min, not fixed: a stack of per-limit ticks is taller than one line
      "& > *": { minHeight: `${DENSITY_ROW_HEIGHT[density]}px` },
    },
  ])
);

/** The class on every StackedLines column (components/datagrid/StackedLines): marks a stacked row. */
export const STACKED_LINES_CLASS = "an-stacked-lines";

/**
 * A row of several limits (StackedLines, getRowHeight "auto"): a one-value cell in it
 * (Component ID, Part Number, Serial Number, an inspection's title) sits on the row's FIRST
 * line, centred where a one-limit row centres it and level with that line's tick and values
 * (Aaron 2026-10-13, C-GQZV Components: rows "tall and empty", ticks "misaligned"). Left to
 * MUI, a dynamic-height cell's `line-height: inherit` put that text ~8px above its line, so the
 * stack read as blank tick-only rows. Only rows that hold a stack: other auto-height grids
 * (wrapped text, F&D) keep their own line height. A stack's own lines set theirs.
 */
const stackedRowFirstLineSx = Object.fromEntries(
  (Object.keys(DENSITY_ROW_HEIGHT) as (keyof typeof DENSITY_ROW_HEIGHT)[]).map((density) => [
    `&.MuiDataGrid-root--density${density} .MuiDataGrid-row--dynamicHeight:has(.${STACKED_LINES_CLASS}) > .MuiDataGrid-cell:not(.MuiDataGrid-cell--flex)`,
    // the row less its 1px border, as a one-line row's cell (MUI: calc(var(--height) - 1px))
    { lineHeight: `${DENSITY_ROW_HEIGHT[density] - 1}px` },
  ])
);

/**
 * The detail-tab grid's spacing (BP-80, PATTERNS.md "Detail-tab grid", Aaron
 * 2026-09-27): inside DetailPremiumBox's 30px top padding the toolbar sits flush --
 * no grid marginTop, no toolbar top padding -- with 20px under + / ACTIONS.
 * AeronetDataGridPremium applies it; a detail tab on a raw DataGridPremium spreads
 * it after commonDataGridPremiumSx (whose list-screen 20px marginTop and 12px
 * toolbar padding are what made the band above the aircraft tabs' toolbars,
 * Aaron 2026-10-06: "there is a gap at the top of all tabs").
 */
export const detailTabGridSx = {
  marginTop: 0,
  "& .MuiDataGrid-toolbarContainer": {
    borderTop: "none !important",
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: "20px",
    paddingLeft: 0,
  },
};

/**
 * A grid of wrapped text rows (getRowHeight "auto", components/datagrid/WrappedTextCell):
 * each row is as tall as its tallest cell, every cell — the tick included — centres in
 * it, and the text keeps the compact row's 8px above and below. First consumer: the
 * Log Book entry's Lines tab (aircraft-services/LineItemsEditor.tsx).
 */
export const wrappedTextRowsSx = {
  "& .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    py: "8px",
  },
  "& .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell--editing": { py: 0 },
};

export const commonDataGridPremiumSx:SxProps<Theme> = {
  ...dataGridHeaderKeylineSx,
  borderLeft: "none",
  borderRight: "none",
  borderTop: "none !important",
  borderRadius: "0",
  flex: 1,
  marginTop: "20px",

  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: "bolder",
    color:"black"
  },

  "& .MuiDataGrid-main": {
    margin: "0px 0px !important",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    borderBottom: `${DATA_GRID_HEADER_KEYLINE} !important`,
    marginBottom: "10px",
  },

  "& .MuiDataGrid-footerContainer": {
    margin: "0px 0px !important",
    borderBottom: `${DATA_GRID_HEADER_KEYLINE} !important`,
    marginBottom: "10px",
  },

  "& .MuiDataGrid-toolbarContainer":{
      alignItems: "center",
      paddingTop: "12px",
      paddingBottom: "12px",
  },
  "& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox": {
    padding: "0 !important",
    justifyContent: "flex-start",
  },
  "& .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root, & .MuiDataGrid-checkboxInput": {
    padding: "0 !important",
    margin: 0,
  },
  // A tick in the FIRST column gets the cell's own inset, so it never sits flush on the grid's
  // (or a tree + grid pane's) left edge (Aaron 2026-10-12, mustang C-GOGR Components: "give
  // the action check box some room"). Behind a reveal chevron the tick keeps 0: the chevron
  // column is its room. GRID_CELL_PADDING_X = a grid cell's own side padding, so the tick lines
  // up with the other columns' text.
  // The doubled class is load-bearing (Aaron 2026-10-13, C-GQZV: "what happened to the cell
  // padding request?"): global.css zeroes `.MuiDataGrid-root .MuiDataGrid-cell[data-field="__check__"]`
  // with !important at the same specificity and loads AFTER these styles (injectFirst), so the
  // single-class form of this rule never rendered (checked in Chromium: 0px).
  [FIRST_COLUMN_TICK_SELECTOR]: {
    paddingLeft: `${GRID_CELL_PADDING_X} !important`,
  },
  ...detailRowControlsOnFirstLineSx,
  ...stackedRowFirstLineSx,
  // Editable-cell affordance: users can't otherwise tell which cells accept
  // inline edits. Hovering an editable cell tints it green with a text cursor;
  // shared here so Build and Respond behave identically.
  "& .MuiDataGrid-cell--editable:hover": {
    backgroundColor: "rgba(46, 125, 50, 0.10)",
    boxShadow: "inset 0 0 0 1px rgba(46, 125, 50, 0.45)",
    cursor: "text",
  },
}

export const commonTableSx:SxProps<Theme> = {
  '& .MuiTableCell-root': {
      borderTop: '2px solid rgba(224, 224, 224, 1)',
      borderBottom: '2px solid rgba(224, 224, 224, 1)',
      // backgroundColor: '#fff'
      paddingTop:"0px !important",
      paddingBottom:"0px !important"
    }

}

   